import React, { useMemo } from 'react'
import { cn, TextButton } from '@yanolja-rnd/nol-design-system'
import { IcError, IcInfo } from '@yanolja-rnd/nol-icons'
import InfoListBottomSheet from '~/app/components-nds/common/info-list-bottom-sheet'
import type { CancellationPolicyClient, CancellationPolicyType } from './types'
import IframeBottomSheet from '~/app/components-nds/common/iframe-bottom-sheet'
import useModalHashV2 from '~/app/components-nds/hooks/use-modal-hash'

type CancellationPolicyProps = CancellationPolicyClient & {
  isFloatingBanner?: boolean // free, free-until 타입인 경우 플로팅 배너 노출 가능
  onClickLog?: () => void | Promise<void>
  className?: string
}

/**
 * 취소규정 타입에 따른 배경색을 반환하는 함수
 */
const getBgColor = (type: CancellationPolicyType) => {
  switch (type) {
    case 'free':
    case 'free-until':
      return 'bg-fill-success-weak'
    case 'fee-charged':
    case 'policy-error':
      return 'bg-fill-warning-weak'
    case 'non-refundable-with-cancel':
    case 'non-refundable':
      return 'bg-fill-danger-weak'
  }
}

/**
 * 취소규정 타입에 따른 텍스트 색상을 반환하는 함수
 */
const getTextColor = (type: CancellationPolicyType) => {
  switch (type) {
    case 'free':
    case 'free-until':
      return 'text-text-success-main'
    case 'fee-charged':
    case 'policy-error':
      return 'text-text-warning-main'
    case 'non-refundable-with-cancel':
    case 'non-refundable':
      return 'text-text-danger-main'
  }
}

/**
 * 취소규정 배지의 내용을 렌더링하는 함수
 */
const renderContent = (
  props: CancellationPolicyProps,
  onClick: () => void,
  shouldShowInfoIcon: boolean
) => {
  const { type, isFloatingBanner } = props

  const infoIcon = shouldShowInfoIcon ? (
    <IcInfo className="flex-shrink-0 cursor-pointer" height={16} width={16} onClick={onClick} />
  ) : null

  switch (type) {
    case 'free':
      return (
        <>
          <span
            className={
              isFloatingBanner
                ? 'typography-body-13-bold pf-pc:typography-subtitle-16-bold'
                : 'typography-body-12-bold pf-pc:typography-body-14-bold'
            }
          >
            무료취소 가능
          </span>
          {infoIcon}
        </>
      )

    case 'free-until':
      return (
        <>
          <span
            className={
              isFloatingBanner
                ? 'typography-body-13-regular pf-pc:typography-subtitle-16-regular'
                : 'typography-body-12-regular-multiline pf-pc:typography-body-14-regular-multiline'
            }
          >{`${props.freeUntilDate} 전까지`}</span>
          <span
            className={
              isFloatingBanner
                ? 'typography-body-13-bold pf-pc:typography-subtitle-16-bold'
                : 'typography-body-12-bold pf-pc:typography-body-14-bold'
            }
          >
            무료취소
          </span>
          {infoIcon}
        </>
      )

    case 'fee-charged':
      return (
        <>
          <span className="typography-body-12-regular-multiline pf-pc:typography-body-14-regular-multiline">{`${props.feeUntilDate} 전까지`}</span>
          <span className="typography-body-12-bold pf-pc:typography-body-14-bold">
            {props.feeText}
          </span>
          {infoIcon}
        </>
      )

    case 'non-refundable-with-cancel':
      return (
        <>
          <span className="typography-body-12-bold pf-pc:typography-body-14-bold">
            취소 및 환불불가
          </span>
          {infoIcon}
        </>
      )

    case 'non-refundable':
      return (
        <>
          <span className="typography-body-12-bold pf-pc:typography-body-14-bold">환불불가</span>
          {infoIcon}
        </>
      )

    case 'policy-error':
      return (
        <div
          className={cn(
            'flex w-full items-center justify-between',
            isFloatingBanner ? 'gap-10 pf-pc:justify-center' : 'gap-8'
          )}
        >
          <div className={cn('flex items-center', isFloatingBanner ? 'gap-4' : 'gap-2')}>
            <IcError
              className={cn(
                'flex-shrink-0',
                isFloatingBanner ? 'size-16 pf-pc:size-20' : 'size-16'
              )}
            />
            <p
              className={cn(
                'flex items-center',
                isFloatingBanner
                  ? 'typography-body-13-bold pf-pc:typography-subtitle-16-bold-multiline'
                  : 'typography-body-12-bold pf-pc:typography-body-14-bold'
              )}
            >
              취소규정 정보를 불러오지 못했어요.
            </p>
          </div>
          <TextButton
            className="flex-shrink-0"
            theme="underline"
            mobileSize="small"
            pcSize={isFloatingBanner ? 'large' : 'small'}
            weight="bold"
            onClick={props.onRefresh}
          >
            새로 고침
          </TextButton>
        </div>
      )

    default:
      return null
  }
}

/**
 * CancellationPolicy Component
 * 공통 > 취소 규정을 표시하는 배지 컴포넌트
 */
const CancellationPolicy: React.FC<CancellationPolicyProps> = props => {
  const { type, isFloatingBanner, onClickLog, className } = props
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useModalHashV2({ id: 'CancellationPolicy' })

  const hasPolicyInfo = type === 'fee-charged' || type === 'free' || type === 'free-until'

  const showPolicyInfo = useMemo(() => {
    if (hasPolicyInfo) {
      return !!props.policyInfo && props.policyInfo.length > 0 && !isFloatingBanner
    }
    return false
  }, [hasPolicyInfo, props, isFloatingBanner])

  const showPolicyLink = useMemo(() => {
    return !!props.policyLink && props.policyLink !== '' && !isFloatingBanner
  }, [props.policyLink, isFloatingBanner])

  const onClickPolicy = async () => {
    await onClickLog?.()
    setIsBottomSheetOpen(true)
  }

  return (
    <>
      <div
        className={cn(
          'flex min-h-42 w-full items-center justify-center gap-2 rounded-12 px-16',
          type === 'policy-error' ? 'py-8' : 'py-12',
          isFloatingBanner && 'min-h-36 rounded-none px-20 py-10 pf-pc:py-16',
          getBgColor(type),
          getTextColor(type),
          className
        )}
      >
        {renderContent(props, onClickPolicy, showPolicyInfo || showPolicyLink)}
      </div>

      {hasPolicyInfo && showPolicyInfo && (
        <InfoListBottomSheet
          isOpen={isBottomSheetOpen}
          onClose={() => setIsBottomSheetOpen(false)}
          header="취소 규정"
          contents={props.policyInfo ?? []}
          dialogClassName="w-[600px]"
        />
      )}
      {showPolicyLink && (
        <IframeBottomSheet
          isOpen={isBottomSheetOpen}
          onClose={() => setIsBottomSheetOpen(false)}
          header="취소 및 환불 규칙"
          iframeUrl={props.policyLink ?? ''}
        />
      )}
    </>
  )
}

export default CancellationPolicy
export type { CancellationPolicyProps }
